/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useEffect, useState } from 'react';
import Beneficiaries from 'domain/Beneficiaries/Beneficiaries';
import { components } from 'generated/payment-initialisation/schema';
import useAlphaSnackbar from 'hooks/useAlphaSnackbar';
import useSwitchAccount from 'hooks/useSwitchAccount';
import { TCountries } from 'models/countries';
import routes from 'routes.path';
import BeneficiariesService from 'services/Beneficiaries/beneficiaries.service';
import RemittersService from 'services/Remitters/remitters.service';
import t from 'utils/translationHelper';

import { CurrencyList } from '@alpha/bene-dtos/lib/responses/CurrencyList';
import { Alert } from '@alpha/ui-lib/ui/Alert';
import { APMainLayout } from '@alpha/ui-lib/ui/MainLayout';
import { StyledTabsWrapper } from '@alpha/ui-lib/ui/Tabs';

import Remitters from './Remitters/Remitters';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';

const MigrationReadiness: React.FC = () => {
  const [remitters, setRemitters] = useState<components['schemas']['RemittersListResponse']>([]);
  const { currentAccount } = useSwitchAccount();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('actionRequired');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [countries, setCountries] = useState<TCountries>([]);
  const [currencies, setCurrencies] = useState<CurrencyList>({ currencies: [], topCurrencies: [] });
  const [tabIndex, setTabIndex] = useState(0);

  const sb = useAlphaSnackbar();

  const getRemitters = async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const remittersResponse = await RemittersService.getRemitters(
        currentAccount?.id, activeTab !== 'allRemitters',
        searchQuery,
        pageNumber,
        signal,
      );
      // Only update state if this request wasn't aborted
      if (!signal?.aborted) {
        setRemitters(remittersResponse.data);
      }
    } catch (error: unknown) {
      // Ignore abort errors
      const isAbortError = error instanceof Error
        && (error.name === 'AbortError' || error.name === 'CanceledError');
      if (isAbortError) {
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      sb.trigger(errorMessage, 'error');
      console.error(error);
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    getRemitters(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [activeTab, pageNumber, searchQuery]);

  async function getCountries() {
    try {
      setCountries(await BeneficiariesService.getCountries());
    } catch (e) {
      sb.trigger(e.response?.data?.error || t('there_was_an_issue_retrieving_countries'));
      console.error({ action: 'Error getting beneficiary countries.' });
    }
  }

  async function getCurrencies() {
    try {
      setCurrencies(await BeneficiariesService.getCurrencies());
    } catch (e) {
      sb.trigger(e.response?.data?.error || t('there_was_an_issue_retrieving_currencies'));
      console.error({ action: 'Error getting beneficiary currencies.' });
    }
  }

  useEffect(() => {
    getCountries();
    getCurrencies();
  }, []);

  useEffect(() => {
    if (tabIndex === 0) {
      setActiveTab('actionRequired');
    }
  }, [tabIndex]);

  return (
    <>
      <APMainLayout
        sticky
        height="165px"
        data-id="migration_center"
        title={(
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <div>{currentAccount.dataCenterVersion === 'Corpay Migration Center' ? t('migration_center') : t('missing_data')}</div>
          </div>
)}
        breadCrumbs={[{ text: t('home'), url: routes.dashboard }]}
        pageTabs={(
          <StyledTabsWrapper
            testId="migration-readiness-tabs"
            tabTitles={[t('beneficiaries'),t('remitters')]}
            tabIndex={tabIndex}
            handleChange={setTabIndex}
          />
        )}
      >
        {currentAccount.dataCenterVersion === 'Corpay Migration Center' && (
        <div style={{ marginTop: '16px' }}>
          {tabIndex === 0 && <Alert variant="info" text={t('migration_center_alert')} />}
          {tabIndex === 1 && <Alert variant="error" text={t('remitters_alert')} overrideIcon={faInfoCircle} />}
        </div>
        )}
        {tabIndex === 0 && (
          <Beneficiaries isMigrationReadiness />
        )}
        {tabIndex === 1 && (
        <Remitters
          remitters={remitters}
          refetchRemitters={getRemitters}
          isLoading={isLoading}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setSearchQuery={setSearchQuery}
          searchQuery={searchQuery}
          setPageNumber={setPageNumber}
          pageNumber={pageNumber}
          countries={countries}
          currencies={currencies}
        />
        )}
      </APMainLayout>
    </>
  );
};

export default MigrationReadiness;
